/**
 * The Plan — 5-tab unified planning page.
 *
 * Tabs: Goals | Planned Income | Planned Expenses | Debt | Assets
 * Each tab answers ONE clear question. No overlap. No duplication.
 *
 * Calm, clear decision space — not a form-filling tool.
 */
import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { CycleHeader } from "../components/rhythm/CycleHeader";
import { GoalsContent } from "./GoalsTab";
import { IncomeContent } from "./IncomePage";
import { PlannedExpensesContent } from "./PlannedExpensesPage";
import { DebtContent } from "./DebtPage";
import { AssetsContent } from "./AssetsPage";
import { PlanHealthSignal } from "../components/guidance/PlanHealthSignal";
import { usePrivacy } from "../components/privacy/PrivacyProvider";
import {
  Target,
  Wallet,
  Receipt,
  TrendingDown,
  Building2,
} from "lucide-react";

const PLAN_TABS = [
  { key: "goals", label: "Goals", icon: Target },
  { key: "income", label: "Planned Income", icon: Wallet },
  { key: "expenses", label: "Planned Expenses", icon: Receipt },
  { key: "debt", label: "Debt", icon: TrendingDown },
  { key: "assets", label: "Assets", icon: Building2 },
];

const TAB_COPY = {
  goals: {
    header: "What are you working toward",
    headerTogether: "What are you working toward together",
    headerSeparate: "What are you building together",
    sub: "Set a clear direction so your money has purpose.",
    micro: "Start with one thing that matters most right now.",
    nextLabel: "Plan your income",
  },
  income: {
    header: "What's coming in this cycle",
    headerTogether: "What's coming in this cycle",
    headerSeparate: "What's coming in this cycle",
    sub: "Know what's available so you can plan with clarity.",
    micro: "Clarity on what's coming in makes everything easier.",
    nextLabel: "Plan your spending",
  },
  expenses: {
    header: "What this cycle needs",
    headerTogether: "What this cycle needs",
    headerSeparate: "What this cycle needs",
    sub: "Start with what must be covered — everything else builds from there.",
    micro: "Cover what matters first — then shape the rest.",
    nextLabel: "Review your debt",
  },
  debt: {
    header: "What you're clearing",
    headerTogether: "What you're clearing together",
    headerSeparate: "What you're clearing",
    sub: "A clear plan helps you reduce pressure over time.",
    micro: "Small, consistent steps reduce pressure over time.",
    nextLabel: "Look at what you're building",
  },
  assets: {
    header: "What you're building",
    headerTogether: "What you're building together",
    headerSeparate: "What you're building",
    sub: "Track what you own and what you're growing over time.",
    micro: "This is what you're building, not just what you have.",
    nextLabel: "Review & reset your cycle",
    nextHref: "/close",
  },
};

function resolveHeader(copy, privacyStyle) {
  if (privacyStyle === "solo") return copy.header;
  if (privacyStyle === "separate_shared_goals") return copy.headerSeparate;
  return copy.headerTogether; // fully_shared and shared_personal
}

export default function AllocationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [planTab, setPlanTab] = useState(searchParams.get("tab") || "goals");
  const { household } = useAuth();
  const { privacyStyle } = usePrivacy();
  const [planState, setPlanState] = useState(null);

  useEffect(() => {
    if (!household?.household_id) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/households/${household.household_id}/plan-health`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPlanState(d); })
      .catch(() => {});
  }, [household?.household_id]);

  useEffect(() => {
    const tabParam = searchParams.get("tab") || "goals";
    if (tabParam !== planTab) setPlanTab(tabParam);
  }, [searchParams]);

  const switchPlanTab = (key) => {
    setPlanTab(key);
    if (key === "goals") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: key });
    }
  };

  const copy = TAB_COPY[planTab] || TAB_COPY.goals;
  const header = resolveHeader(copy, privacyStyle);
  const currentIdx = PLAN_TABS.findIndex(t => t.key === planTab);
  const hasNext = currentIdx < PLAN_TABS.length - 1;

  return (
    <AppLayout>
      <div data-testid="allocation-page">
        {/* Tab navigation — gradient flow system */}
        <div className="border-b border-border/40 bg-card/80 sticky top-0 z-10 backdrop-blur-md">
          <div className="flex items-center gap-4 px-6 py-3.5 overflow-x-auto scrollbar-hide">
            <h1 className="font-heading text-lg font-semibold text-foreground shrink-0">The Plan</h1>
            <nav
              className="flex items-center rounded-full shrink-0 p-[3px]"
              style={{ background: 'linear-gradient(to right, rgba(26,77,62,0.04), rgba(26,77,62,0.08), rgba(26,77,62,0.14), rgba(26,77,62,0.22), rgba(26,77,62,0.32))' }}
              data-testid="plan-tabs"
              role="tablist"
            >
              {PLAN_TABS.map((tab, idx) => {
                const isActive = planTab === tab.key;
                const depth = [0.08, 0.16, 0.28, 0.45, 0.65][idx];
                const textOpacity = [0.7, 0.75, 0.85, 0.92, 1.0][idx];
                const iconOpacity = [0.5, 0.6, 0.7, 0.85, 1.0][idx];

                return (
                  <button
                    key={tab.key}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => switchPlanTab(tab.key)}
                    className={`
                      relative flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-full
                      cursor-pointer whitespace-nowrap select-none
                      transition-all duration-200 ease-out
                      ${isActive
                        ? 'font-semibold shadow-[0_1px_4px_rgba(26,77,62,0.18),inset_0_0_0_1px_rgba(26,77,62,0.15)]'
                        : 'font-medium hover:-translate-y-px hover:shadow-[0_2px_6px_rgba(26,77,62,0.1)] hover:brightness-110'
                      }
                    `}
                    style={{
                      backgroundColor: `rgba(26,77,62,${isActive ? depth + 0.05 : depth})`,
                      color: `rgba(26,77,62,${isActive ? 1 : textOpacity})`,
                    }}
                    data-testid={`plan-tab-${tab.key}`}
                  >
                    <tab.icon
                      className="w-3.5 h-3.5 transition-opacity duration-200"
                      style={{ opacity: isActive ? 1 : iconOpacity }}
                    />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Header */}
        <div className="px-6 py-5 bg-[#1a4d3e]/[0.03] border-b border-[#1a4d3e]/10">
          <div className="max-w-4xl mx-auto flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground leading-tight" data-testid="plan-header">
                {header}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5 leading-snug" data-testid="plan-subtext">
                {copy.sub}
              </p>
            </div>
            {copy.nextLabel && (
              copy.nextHref ? (
                <Link
                  to={copy.nextHref}
                  className="flex items-center gap-1 text-xs font-medium whitespace-nowrap text-[#1a4d3e] hover:text-[#1a4d3e]/70 transition-colors mt-1"
                  data-testid="plan-next-step"
                >
                  Next step → {copy.nextLabel}
                </Link>
              ) : hasNext ? (
                <button
                  onClick={() => switchPlanTab(PLAN_TABS[currentIdx + 1].key)}
                  className="flex items-center gap-1 text-xs font-medium whitespace-nowrap text-[#1a4d3e] hover:text-[#1a4d3e]/70 transition-colors mt-1"
                  data-testid="plan-next-step"
                >
                  Next step → {copy.nextLabel}
                </button>
              ) : null
            )}
          </div>
        </div>

        {/* Cycle Header */}
        <div className="px-6 pt-4">
          <CycleHeader />
        </div>

        {/* Plan Health Signal */}
        <div className="px-6 lg:px-8 pt-4">
          <PlanHealthSignal />
        </div>

        {/* Micro guidance — one line per tab */}
        <div className="px-6 lg:px-8 pt-1 pb-0">
          <p className="text-xs text-muted-foreground/70 italic" data-testid="plan-micro-guidance">
            {copy.micro}
          </p>
        </div>

        {/* First-time guidance — only when no goals, no income, no expenses */}
        {planTab === "goals" && planState?.state === "not_set_up" && planState?.goal_count === 0 && planState?.income_count === 0 && planState?.allocation_count === 0 && (
          <div className="px-6 lg:px-8 pt-3" data-testid="plan-first-time-guidance">
            <p className="text-xs text-[#1a4d3e] font-medium">
              You can move through this one step at a time — start here.
            </p>
          </div>
        )}

        {/* Completion feel — when user has goal + income + expenses */}
        {planState && planState.goal_count > 0 && planState.income_count > 0 && planState.allocation_count > 0 && planState.state === "on_track" && (
          <div className="px-6 lg:px-8 pt-3" data-testid="plan-completion-message">
            <p className="text-xs text-[#1a4d3e] font-medium">
              You've built your first plan
            </p>
          </div>
        )}

        {/* Tab content */}
        {planTab === "goals" && <GoalsContent />}
        {planTab === "income" && <IncomeContent />}
        {planTab === "expenses" && <PlannedExpensesContent />}
        {planTab === "debt" && <DebtContent />}
        {planTab === "assets" && <AssetsContent />}
      </div>
    </AppLayout>
  );
}
